import React, {useContext, useEffect, useMemo, useState} from "react";
import Cart from "../../../components/app/Cart/Cart";
import CartItemsContext from "../../../context/cartItems/CartItemsContext";
import RecentlyViewedContext from "../../../context/recentlyViewed/RecentlyViewedContext";
import LikedItemsContext from "../../../context/likedItems/LikedItemsContext";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ProductsContainer from "../../../components/app/Products/ProductsContainer";
import {styled} from "@mui/material/styles";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import RecommendOutlinedIcon from "@mui/icons-material/RecommendOutlined";
import CustomButton from "../../../components/common/Button/CustomButton";
import {useNavigate} from "react-router-dom";
import AllProductsContext from "../../../context/allProducts/AllProductsContext";
import Loader from "../../../components/loader/Loader";

const Root = styled("div")(({theme}) => ({
  flexGrow: 1,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(10), // safe space for mobile sticky bar & navigation
  [theme.breakpoints.up("md")]: {
    paddingBottom: theme.spacing(6),
  },
}));

const CartPage = () => {
  const cartItemsContext = useContext(CartItemsContext);
  const cartItemsArray = cartItemsContext?.state || [];
  const recentlyViewedContext = useContext(RecentlyViewedContext);
  const likedItemsContext = useContext(LikedItemsContext);
  const allProductsContext = useContext(AllProductsContext);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const likedItemsArray = useMemo(() => {
    const rawLikedItems = likedItemsContext?.state || [];
    if (!rawLikedItems || rawLikedItems.length === 0) return [];
    const allProducts = allProductsContext?.state || [];
    if (allProducts.length === 0) return rawLikedItems;
    return rawLikedItems.map((item) => {
      const matched = allProducts.find((p) => p.id === item.id);
      return matched ? {...matched} : item;
    });
  }, [likedItemsContext?.state, allProductsContext?.state]);

  const recentlyViewedItemsArray = useMemo(() => {
    const rawRecentlyViewed = recentlyViewedContext?.state || [];
    if (!rawRecentlyViewed || rawRecentlyViewed.length === 0) return [];
    const allProducts = allProductsContext?.state || [];
    if (allProducts.length === 0) return rawRecentlyViewed;
    return rawRecentlyViewed.map((item) => {
      const matched = allProducts.find((p) => p.id === item.id);
      return matched ? {...matched} : item;
    });
  }, [recentlyViewedContext?.state, allProductsContext?.state]);

  useEffect(() => {
    if (cartItemsContext.state !== null) {
      setLoading(false);
    } else {
      const timer = setTimeout(() => {
        setLoading(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [cartItemsContext.state]);

  const isCartEmpty = !cartItemsArray || cartItemsArray.length === 0;

  return (
    <>
      {loading && <Loader type="LINEAR" fullHeight={false} />}
      {!loading && (
        <Root>
          <Container maxWidth="xl" sx={{px: {xs: 1.5, sm: 3, md: 4}}}>
            {/* Empty Cart View */}
            {isCartEmpty && (
              <Box sx={{py: {xs: 3, md: 5}, display: "flex", justifyContent: "center"}}>
                <Paper
                  elevation={0}
                  sx={{
                    width: "100%",
                    maxWidth: 680,
                    textAlign: "center",
                    p: {xs: 3, sm: 5},
                    borderRadius: "20px",
                    border: "1px solid #e2e8f0",
                    background: "linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%)",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
                  }}
                >
                  {/* Icon Badge */}
                  <Box
                    sx={{
                      width: 90,
                      height: 90,
                      borderRadius: "50%",
                      bgcolor: "rgba(47, 93, 126, 0.08)",
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2.5,
                      boxShadow: "0 4px 12px rgba(47, 93, 126, 0.12)",
                    }}
                  >
                    <ShoppingBagOutlinedIcon sx={{fontSize: 48, color: "primary.main"}} />
                  </Box>

                  <Typography
                    variant="h5"
                    component="h1"
                    sx={{fontWeight: 700, mb: 1, color: "text.primary"}}
                  >
                    No items in cart
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                      maxWidth: 480,
                      mx: "auto",
                      mb: 3.5,
                      lineHeight: 1.6,
                    }}
                  >
                    Your shopping cart is currently empty. Explore our handcrafted
                    ayurvedic products and find what suits your wellness journey best.
                  </Typography>

                  {/* Primary & Secondary Action Buttons */}
                  <Stack
                    direction={{xs: "column", sm: "row"}}
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                    sx={{mb: 0}}
                  >
                    <CustomButton
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate("/collections")}
                      sx={{
                        px: 4,
                        py: 1.25,
                        borderRadius: "10px",
                        fontWeight: 600,
                        textTransform: "none",
                      }}
                    >
                      Explore Products
                    </CustomButton>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<FavoriteBorderIcon />}
                      onClick={() => navigate("/myWishlist")}
                      sx={{
                        px: 3,
                        py: 1.25,
                        borderRadius: "10px",
                        fontWeight: 600,
                        textTransform: "none",
                        color: "text.primary",
                        borderColor: "#cbd5e1",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "rgba(47, 93, 126, 0.04)",
                        },
                      }}
                    >
                      View Wishlist
                    </Button>
                  </Stack>
                </Paper>
              </Box>
            )}

            {/* Cart with Items */}
            {!isCartEmpty && (
              <Cart products={cartItemsArray} />
            )}

            {/* Wishlist Items (When Cart is Empty and Available) */}
            {isCartEmpty && likedItemsArray.length > 0 && (
              <Box sx={{mt: {xs: 4, md: 6}}}>
                <Divider sx={{mb: 3}} />
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{mb: 2}}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FavoriteBorderIcon color="primary" />
                    <Typography variant="h6" sx={{fontWeight: 600}}>
                      From Your Wishlist
                    </Typography>
                    <Chip
                      label={likedItemsArray.length}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        height: 22,
                        bgcolor: "rgba(47, 93, 126, 0.1)",
                        color: "primary.main",
                      }}
                    />
                  </Stack>
                  <Button
                    variant="text"
                    size="small"
                    endIcon={<ArrowForwardIcon sx={{fontSize: 16}} />}
                    onClick={() => navigate("/myWishlist")}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      color: "primary.main",
                      "&:hover": { bgcolor: "rgba(47, 93, 126, 0.06)" },
                    }}
                  >
                    View All
                  </Button>
                </Stack>
                <ProductsContainer
                  showInRow={true}
                  addToCartEnable={true}
                  showCarousel={false}
                  showLikeButton={true}
                  productsArray={likedItemsArray}
                  showRatingNumber={false}
                />
              </Box>
            )}

            {/* Recently Viewed Items */}
            {recentlyViewedItemsArray.length > 0 && (
              <Box sx={{mt: {xs: 4, md: 6}}}>
                <Divider sx={{mb: 3}} />
                <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 2}}>
                  <HistoryOutlinedIcon color="primary" />
                  <Typography variant="h6" sx={{fontWeight: 600}}>
                    Recently Viewed
                  </Typography>
                  <Chip
                    label={recentlyViewedItemsArray.length}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      height: 22,
                      bgcolor: "rgba(47, 93, 126, 0.1)",
                      color: "primary.main",
                    }}
                  />
                </Stack>
                <ProductsContainer
                  showInRow={true}
                  addToCartEnable={true}
                  showCarousel={false}
                  showLikeButton={true}
                  productsArray={recentlyViewedItemsArray}
                  showRatingNumber={false}
                />
              </Box>
            )}

            {/* Consider these products */}
            {allProductsContext?.state && allProductsContext.state.length > 0 && (
              <Box sx={{mt: {xs: 4, md: 6}}}>
                <Divider sx={{mb: 3}} />
                <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 2}}>
                  <RecommendOutlinedIcon color="primary" />
                  <Typography variant="h6" sx={{fontWeight: 600}}>
                    You Might Also Like
                  </Typography>
                </Stack>
                <ProductsContainer
                  showInRow={true}
                  addToCartEnable={true}
                  showCarousel={false}
                  showLikeButton={true}
                  productsArray={allProductsContext.state}
                  showRatingNumber={false}
                />
              </Box>
            )}
          </Container>
        </Root>
      )}
    </>
  );
};

export default CartPage;

